/**
 * LA GUARDIA SULL'ALTEZZA DELLA RADICE — `#root` non puo' schiacciarsi a una striscia (r16, I-438).
 *
 * `#root` e' alto `var(--tg-viewport-stable-height, 100%)` (`styles/globals.css`) e quella variabile la scrive
 * `viewport.bindCssVars()` (`lib/telegram.ts`) con cio' che l'SDK sa dell'altezza visibile. Due fatti dell'SDK
 * (`@tma.js/sdk` 3.2.0, `dist/index.js`) la rendono capace di restare SBAGLIATA per sempre:
 *  · al mount, `stableHeight: is_state_stable ? height : 0` — se Telegram risponde «non stabile», la variabile vale `0px`;
 *  · su un RELOAD (`navigation.type === 'reload'`) il viewport NON si richiede: si ripristina da `sessionStorage`
 *    (`tapps/viewport`) il valore di prima — guasto compreso. Ricaricare non cura, rimette lo stesso numero.
 *
 * Con una radice di 0-140 px lo shell resta una TabBar in alto e nient'altro: `main` schiacciato a 0-12 px, banner e barre
 * tagliati da `overflow: hidden` — e nessuna schermata puo' disegnare niente, per quanto onesta sia. [M stg-tma, 2026-10-05:
 * stable-height 56px → TabBar a y=8 su 844; main svuotato a radice NORMALE → TabBar a y=796: il «menu in alto» e' la radice,
 * non il `main` vuoto.] Che il Telegram di un telefono abbia davvero mandato quel valore e' [D]: dal server non si vede.
 *
 * ⚠️ La soglia e' un RAPPORTO sulla finestra, non un numero: la mini app collassata sta a circa il 60% e quello e' un valore
 * VERO (la barra deve restare sul bordo visibile, per questo `stable` e non `height`) — non va toccato. Sotto il 40% non c'e'
 * una mini app collassata che si possa usare: e' un valore guasto. In quel caso la radice torna a `100%`, cioe' il ripiego
 * che `globals.css` gia' dichiara per il browser e per i client senza viewport: lo schermo intero, non una scheda a meta'.
 */
export const ALTEZZA_STABILE_MIN_RAPPORTO = 0.4;

/**
 * `true` se l'altezza stabile e' credibile (o se non si puo' giudicare). Pura.
 *
 * ⚠️ «Non si sa» = credibile: un valore non finito (`NaN`, una WebView non ancora disposta) non
 * autorizza a togliere alla radice l'altezza che Telegram le ha dato — la guardia agisce solo su un numero che SI LEGGE ed
 * e' sbagliato.
 */
export function altezzaStabilePlausibile(stabile: number, finestra: number): boolean {
  if (!Number.isFinite(stabile) || !Number.isFinite(finestra)) return true;
  return stabile >= finestra * ALTEZZA_STABILE_MIN_RAPPORTO;
}

/**
 * Applica la guardia alla radice: altezza guasta ⇒ `height: 100%` INLINE (vince sul CSS e non tocca la variabile, che l'SDK
 * continua a scrivere); altezza sana ⇒ inline tolto, torna la regola del CSS. Idempotente: si richiama a ogni cambio.
 * Ritorna se ha ignorato il valore, per chi vuole dirlo.
 */
export function applicaAltezzaRadice(
  radice: HTMLElement | null,
  stabile: number,
  finestra: number,
): 'sana' | 'ignorata' {
  const sana = altezzaStabilePlausibile(stabile, finestra);
  if (radice) radice.style.height = sana ? '' : '100%';
  return sana ? 'sana' : 'ignorata';
}
